import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, TrendingDown, CheckCircle2, AlertTriangle, Loader2, ChevronRight, BarChart3, FileText, Target, Cpu, Settings, Search, Lock, Calendar, Mail } from 'lucide-react';
import { getResults, exportPDF } from '../utils/api';

// Blurred Content Overlay Component
const BlurredOverlay = ({ children, showCTA = true }) => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleEmailClick = () => {
    const subject = encodeURIComponent('Request for Full Readiness Report Review');
    const body = encodeURIComponent('Hi FMiQ Team,\n\nI recently completed the FM Operations Readiness Survey and would like to schedule a 30-minute meeting to review my full results with your experts.\n\nPlease let me know your available times.\n\nThank you!');
    window.open(`mailto:sales@fmiqs.com?subject=${subject}&body=${body}`, '_blank');
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Simulate form submission - in production, this would send to your backend
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div className="relative">
      <div className="blur-sm pointer-events-none select-none">
        {children}
      </div>
      {showCTA && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-transparent via-white/80 to-white/95">
          <div className="text-center p-6 max-w-md">
            <div className="w-16 h-16 bg-primary-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary-blue" />
            </div>
            <h3 className="text-xl font-inter font-bold text-deep-navy mb-2">
              Unlock Your Full Report
            </h3>
            <p className="text-slate-grey font-lato mb-6">
              Schedule a free 30-minute consultation with our FM experts to review your complete analysis, including detailed recommendations and ROI estimates.
            </p>
            
            {!showContactForm ? (
              <div className="space-y-3">
                <button
                  onClick={handleEmailClick}
                  className="w-full flex items-center justify-center gap-2 bg-action-amber hover:bg-amber-500 text-white font-inter font-semibold py-3 px-6 rounded-lg transition-colors"
                  data-testid="unlock-email-btn"
                >
                  <Mail className="w-5 h-5" />
                  Email Us to Schedule
                </button>
                <button
                  onClick={() => setShowContactForm(true)}
                  className="w-full flex items-center justify-center gap-2 bg-primary-blue hover:bg-blue-600 text-white font-inter font-semibold py-3 px-6 rounded-lg transition-colors"
                  data-testid="unlock-form-btn"
                >
                  <Calendar className="w-5 h-5" />
                  Request a Callback
                </button>
              </div>
            ) : submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-inter font-medium">Thank you! We'll contact you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmitForm} className="space-y-3 text-left">
                <input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-lato"
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-lato"
                />
                <textarea
                  placeholder="Best time to reach you (optional)"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-lato resize-none"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowContactForm(false)}
                    className="flex-1 py-2 px-4 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 font-inter"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-2 px-4 bg-action-amber hover:bg-amber-500 text-white rounded-lg font-inter font-semibold disabled:opacity-50"
                  >
                    {submitting ? 'Sending...' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Lifecycle Wheel Component - visualizes the 5 categories as a continuous cycle
const LifecycleWheel = ({ categoryScores }) => {
  const categories = [
    { id: 'asset_readiness', name: 'Asset\nReadiness', shortName: 'Assets', phase: 'Foundation' },
    { id: 'maintenance_readiness', name: 'Maintenance\nReadiness', shortName: 'Maintenance', phase: 'Operations' },
    { id: 'capital_planning_readiness', name: 'Capital\nPlanning', shortName: 'Capital', phase: 'Planning' },
    { id: 'compliance_risk_readiness', name: 'Compliance\n& Risk', shortName: 'Compliance', phase: 'Governance' },
    { id: 'data_governance_readiness', name: 'Data\nGovernance', shortName: 'Data', phase: 'Sustainability' }
  ];

  const getScoreColor = (score) => {
    if (score >= 4) return { fill: '#22c55e', stroke: '#16a34a', bg: '#dcfce7' };
    if (score >= 3) return { fill: '#F59E0B', stroke: '#D97706', bg: '#FEF3C7' }; // Action Amber
    if (score >= 2) return { fill: '#f97316', stroke: '#ea580c', bg: '#fed7aa' };
    return { fill: '#ef4444', stroke: '#dc2626', bg: '#fecaca' };
  };

  const centerX = 200;
  const centerY = 200;
  const outerRadius = 160;
  const innerRadius = 80;
  const segmentGap = 2;

  // Calculate segment paths
  const createSegmentPath = (index, total) => {
    const anglePerSegment = (2 * Math.PI) / total;
    const startAngle = index * anglePerSegment - Math.PI / 2 + (segmentGap * Math.PI / 180);
    const endAngle = (index + 1) * anglePerSegment - Math.PI / 2 - (segmentGap * Math.PI / 180);

    const outerStart = {
      x: centerX + outerRadius * Math.cos(startAngle),
      y: centerY + outerRadius * Math.sin(startAngle)
    };
    const outerEnd = {
      x: centerX + outerRadius * Math.cos(endAngle),
      y: centerY + outerRadius * Math.sin(endAngle)
    };
    const innerStart = {
      x: centerX + innerRadius * Math.cos(endAngle),
      y: centerY + innerRadius * Math.sin(endAngle)
    };
    const innerEnd = {
      x: centerX + innerRadius * Math.cos(startAngle),
      y: centerY + innerRadius * Math.sin(startAngle)
    };

    return `M ${outerStart.x} ${outerStart.y} 
            A ${outerRadius} ${outerRadius} 0 0 1 ${outerEnd.x} ${outerEnd.y} 
            L ${innerStart.x} ${innerStart.y} 
            A ${innerRadius} ${innerRadius} 0 0 0 ${innerEnd.x} ${innerEnd.y} Z`;
  };

  const getLabelPosition = (index, total) => {
    const anglePerSegment = (2 * Math.PI) / total;
    const midAngle = (index + 0.5) * anglePerSegment - Math.PI / 2;
    const labelRadius = (outerRadius + innerRadius) / 2;
    return {
      x: centerX + labelRadius * Math.cos(midAngle),
      y: centerY + labelRadius * Math.sin(midAngle)
    };
  };

  const getScorePosition = (index, total) => {
    const anglePerSegment = (2 * Math.PI) / total;
    const midAngle = (index + 0.5) * anglePerSegment - Math.PI / 2;
    const scoreRadius = outerRadius + 25;
    return {
      x: centerX + scoreRadius * Math.cos(midAngle),
      y: centerY + scoreRadius * Math.sin(midAngle)
    };
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 mb-8" data-testid="lifecycle-wheel">
      <h2 className="text-xl font-inter font-bold text-deep-navy mb-2 flex items-center gap-2">
        <svg className="w-6 h-6 text-primary-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        FM Operations Lifecycle
      </h2>
      <p className="text-sm text-slate-grey font-lato mb-6">Your readiness across the continuous improvement cycle</p>
      
      <div className="flex justify-center">
        <svg width="400" height="400" viewBox="0 0 400 400" className="max-w-full">
          {/* Decorative outer ring */}
          <circle cx={centerX} cy={centerY} r={outerRadius + 8} fill="none" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="4 4" />
          
          {/* Arrow indicators showing cycle direction */}
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#9ca3af" />
            </marker>
          </defs>
          
          {/* Cycle direction arc */}
          <path
            d={`M ${centerX + outerRadius + 20} ${centerY} A ${outerRadius + 20} ${outerRadius + 20} 0 0 1 ${centerX} ${centerY - outerRadius - 20}`}
            fill="none"
            stroke="#d1d5db"
            strokeWidth="2"
            markerEnd="url(#arrowhead)"
            opacity="0.6"
          />

          {/* Category segments */}
          {categories.map((cat, index) => {
            const score = categoryScores?.[cat.id]?.final_score || 0;
            const colors = getScoreColor(score);
            const labelPos = getLabelPosition(index, categories.length);
            const scorePos = getScorePosition(index, categories.length);

            return (
              <g key={cat.id}>
                {/* Segment */}
                <path
                  d={createSegmentPath(index, categories.length)}
                  fill={colors.bg}
                  stroke={colors.stroke}
                  strokeWidth="2"
                  className="transition-all duration-300 hover:opacity-80 cursor-pointer"
                />
                
                {/* Category label */}
                <text
                  x={labelPos.x}
                  y={labelPos.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-xs font-medium fill-gray-700 pointer-events-none"
                  style={{ fontSize: '10px' }}
                >
                  {cat.shortName}
                </text>

                {/* Score badge */}
                <g transform={`translate(${scorePos.x}, ${scorePos.y})`}>
                  <circle r="18" fill={colors.fill} className="drop-shadow-sm" />
                  <text
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="fill-white font-bold"
                    style={{ fontSize: '11px' }}
                  >
                    {score ? score.toFixed(1) : '-'}
                  </text>
                </g>
              </g>
            );
          })}

          {/* Center circle with overall score */}
          <circle cx={centerX} cy={centerY} r={innerRadius - 10} fill="#0F172A" />
          <circle cx={centerX} cy={centerY} r={innerRadius - 15} fill="#0F172A" stroke="#F59E0B" strokeWidth="3" />
          <text x={centerX} y={centerY - 15} textAnchor="middle" className="fill-gray-300" style={{ fontSize: '10px' }}>
            OVERALL
          </text>
          <text x={centerX} y={centerY + 8} textAnchor="middle" className="fill-white font-bold" style={{ fontSize: '24px' }}>
            {categoryScores ? (Object.values(categoryScores).reduce((sum, c) => sum + (c.final_score || 0), 0) / 5).toFixed(2) : '-'}
          </text>
          <text x={centerX} y={centerY + 25} textAnchor="middle" className="fill-gray-400" style={{ fontSize: '10px' }}>
            / 5.00
          </text>
        </svg>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-green-500"></span>
          <span className="text-gray-600">Strong (4-5)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
          <span className="text-gray-600">Moderate (3-4)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-orange-500"></span>
          <span className="text-gray-600">Developing (2-3)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-500"></span>
          <span className="text-gray-600">Needs Attention (1-2)</span>
        </div>
      </div>

      {/* Lifecycle phases description */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500 text-center italic">
          The FM lifecycle flows from <strong>Asset Foundation</strong> → <strong>Maintenance Operations</strong> → <strong>Capital Planning</strong> → <strong>Compliance Governance</strong> → <strong>Data Sustainability</strong> → and back to Assets as the cycle resets with improved data and processes.
        </p>
      </div>
    </div>
  );
};

const CATEGORY_NAMES = {
  asset_readiness: 'Asset Readiness',
  maintenance_readiness: 'Maintenance Readiness',
  capital_planning_readiness: 'Capital Planning Readiness',
  compliance_risk_readiness: 'Compliance & Risk Readiness',
  data_governance_readiness: 'Data Governance & Sustainability'
};

const QUESTION_TITLES = {
  asset_readiness: {
    a: 'Completeness of Asset Inventory',
    b: 'Required Fields Present',
    c: 'Structure & Hierarchy Consistency',
    d: 'Confidence / Validation Method',
    e: 'Currency (How Recently Updated)'
  },
  maintenance_readiness: {
    a: 'Work Order Process',
    b: 'PM Program Coverage & Scheduling',
    c: 'Work Order Data Quality',
    d: 'Resourcing & Execution Reliability',
    e: 'Reporting / KPIs / Auditability'
  },
  capital_planning_readiness: {
    a: 'Asset Lifecycle Data Availability',
    b: 'Condition Assessment Inputs',
    c: 'Cost Estimation Capability',
    d: 'Prioritization & Decision Framework',
    e: 'Capital Plan Output & Governance'
  },
  compliance_risk_readiness: {
    a: 'Compliance Inventory & Scope Coverage',
    b: 'Inspection / Testing / Certification Tracking',
    c: 'Corrective Action Management',
    d: 'Documentation & Audit Readiness',
    e: 'Risk Prioritization & Criticality'
  },
  data_governance_readiness: {
    a: 'Data Standards',
    b: 'Ownership & Accountability',
    c: 'Change Control',
    d: 'Data Quality Monitoring',
    e: 'Enablement & Adoption'
  }
};

function ResultsPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  
  // Check if report should be unlocked (for admin viewing)
  const isUnlocked = searchParams.get('unlock') === 'true';

  useEffect(() => {
    const loadResults = async () => {
      try {
        const data = await getResults(sessionId);
        if (!data.rollup?.completed) {
          navigate(`/survey/${sessionId}`);
          return;
        }
        setResults(data);
      } catch (error) {
        console.error('Failed to load results:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    loadResults();
  }, [sessionId, navigate]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const blob = await exportPDF(sessionId, isUnlocked);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `readiness_report_${sessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4) return 'text-green-600 bg-green-100';
    if (score >= 3) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreBgColor = (score) => {
    if (score >= 4) return 'bg-green-500';
    if (score >= 3) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getEvidenceLabel = (strength) => {
    if (strength <= 1) return { label: 'Weak', color: 'text-red-600' };
    if (strength <= 3) return { label: 'Moderate', color: 'text-yellow-600' };
    return { label: 'Strong', color: 'text-green-600' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cloud-grey">
        <Loader2 className="w-8 h-8 animate-spin text-primary-blue" />
      </div>
    );
  }

  const rollup = results?.rollup;

  return (
    <div className="min-h-screen bg-cloud-grey">
      {/* Header */}
      <header className="bg-deep-navy text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate('/')}
            className="mb-6 text-slate-grey hover:text-white flex items-center gap-2 font-inter"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl font-inter font-bold mb-2" data-testid="results-title">
                FM Readiness Assessment Results
              </h1>
              <p className="text-slate-grey font-lato">
                {results?.org_name || 'Your Organization'} • {results?.contact_name || ''} • Generated {new Date().toLocaleDateString()}
              </p>
              {results?.email && (
                <p className="text-slate-grey/60 text-sm font-lato mt-1">
                  Results sent to: {results.email}
                </p>
              )}
            </div>
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-action-amber text-deep-navy font-inter font-semibold rounded-lg hover:bg-accent-600 transition-all disabled:opacity-50"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              {downloading ? 'Generating...' : 'Download PDF Report'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Email Confirmation Banner */}
        {results?.email_sent && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="email-sent-banner">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-green-800 font-inter font-medium">Results emailed successfully!</p>
              <p className="text-green-600 text-sm font-lato">A copy of your results has been sent to {results.email}</p>
            </div>
          </div>
        )}

        {/* Overall Score Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 mb-8" data-testid="overall-score-card">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Readiness Index */}
            <div className="text-center">
              <div className="text-sm text-slate-grey font-inter uppercase tracking-wide mb-2">Overall Readiness Index</div>
              <div className={`inline-flex items-center justify-center w-28 h-28 rounded-full text-3xl font-inter font-bold ${getScoreColor(rollup?.overall_readiness_index)}`} data-testid="overall-index">
                {typeof rollup?.overall_readiness_index === 'number' ? rollup.overall_readiness_index.toFixed(2) : '-'}
              </div>
              <div className="text-slate-grey font-lato mt-2">out of 5.00</div>
            </div>

            {/* Evidence Strength */}
            <div className="text-center">
              <div className="text-sm text-slate-grey font-inter uppercase tracking-wide mb-2">Evidence Strength</div>
              <div className={`text-4xl font-inter font-bold ${getEvidenceLabel(rollup?.overall_evidence_strength).color}`} data-testid="evidence-label">
                {rollup?.evidence_label}
              </div>
              <div className="text-slate-grey font-lato mt-2">{typeof rollup?.overall_evidence_strength === 'number' ? rollup.overall_evidence_strength.toFixed(2) : 0}/5 questions supported</div>
            </div>

            {/* Priority Focus */}
            <div className="text-center">
              <div className="text-sm text-slate-grey font-inter uppercase tracking-wide mb-2">Priority Focus Areas</div>
              <div className="space-y-2" data-testid="priority-areas">
                {rollup?.priority_categories?.slice(0, 2).map((cat, idx) => (
                  <div key={cat} className="flex items-center justify-center gap-2">
                    <span className="w-6 h-6 bg-action-amber/20 text-action-amber rounded-full flex items-center justify-center text-sm font-inter font-medium">
                      {idx + 1}
                    </span>
                    <span className="text-sm font-inter font-medium text-deep-navy">{CATEGORY_NAMES[cat]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        <div className="bg-gradient-to-r from-primary-50 to-sky-50 rounded-xl border border-primary-200 p-6 mb-8" data-testid="executive-summary">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-primary-blue" />
            </div>
            <div>
              <h2 className="text-lg font-inter font-semibold text-deep-navy mb-2">Executive Summary</h2>
              <p className="text-slate-700 font-lato leading-relaxed">{rollup?.executive_summary}</p>
            </div>
          </div>
        </div>

        {/* AI-Enhanced Insights Section */}
        {results?.ai_insights && !results.ai_insights.error && (
          <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 rounded-xl border border-violet-200 p-6 mb-8" data-testid="ai-insights">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-inter font-semibold text-deep-navy">AI-Enhanced Analysis</h2>
                <p className="text-xs text-violet-600 font-lato">Powered by advanced AI review of your assessment data</p>
              </div>
            </div>

            {/* Enhanced Executive Summary */}
            {results.ai_insights.enhanced_executive_summary && (
              <div className="bg-white/60 rounded-lg p-4 mb-4 border border-violet-100">
                <h3 className="text-sm font-inter font-semibold text-violet-800 mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-violet-500 rounded-full"></span>
                  Enhanced Summary
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                  {results.ai_insights.enhanced_executive_summary}
                </p>
              </div>
            )}

            {/* Key Findings */}
            {results.ai_insights.key_findings && results.ai_insights.key_findings.length > 0 && (
              <div className="bg-white/60 rounded-lg p-4 mb-4 border border-violet-100">
                <h3 className="text-sm font-semibold text-violet-800 mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-violet-500 rounded-full"></span>
                  Key Findings
                </h3>
                <ul className="space-y-2">
                  {results.ai_insights.key_findings.map((finding, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-violet-500 mt-1">•</span>
                      <span>{finding}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Evidence Insights */}
            {results.ai_insights.evidence_insights && (
              <div className="bg-white/60 rounded-lg p-4 mb-4 border border-violet-100">
                <h3 className="text-sm font-semibold text-violet-800 mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-violet-500 rounded-full"></span>
                  Evidence Analysis
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {results.ai_insights.evidence_insights}
                </p>
              </div>
            )}

            {/* Hidden Risks */}
            {results.ai_insights.hidden_risks && (
              <div className="bg-amber-50/80 rounded-lg p-4 mb-4 border border-amber-200">
                <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Potential Risks Identified
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {results.ai_insights.hidden_risks}
                </p>
              </div>
            )}

            {/* AI-Enhanced Recommendations - BLURRED unless unlocked */}
            {results.ai_insights.enhanced_recommendations && results.ai_insights.enhanced_recommendations.length > 0 && (
              isUnlocked ? (
                <div className="bg-white/60 rounded-lg p-4 border border-violet-100">
                  <h3 className="text-sm font-semibold text-violet-800 mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-violet-500 rounded-full"></span>
                    AI-Powered Recommendations with ROI Estimates
                  </h3>
                  <div className="space-y-3">
                    {results.ai_insights.enhanced_recommendations.slice(0, 4).map((rec, idx) => (
                      <div key={idx} className="border-l-2 border-violet-300 pl-3 pb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-violet-600 bg-violet-100 px-2 py-0.5 rounded">
                            #{rec.priority || idx + 1}
                          </span>
                          <span className="text-sm font-medium text-gray-800">{rec.recommendation}</span>
                        </div>
                        {rec.rationale && (
                          <p className="text-xs text-gray-600 mb-1">{rec.rationale}</p>
                        )}
                        {rec.evidence_cited && (
                          <p className="text-xs text-violet-600 italic mb-2">
                            📊 Based on: {rec.evidence_cited}
                          </p>
                        )}
                        {/* Cost Impact Badge */}
                        {(rec.estimated_cost_impact || rec.payback_timeframe) && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {rec.estimated_cost_impact && (
                              <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {rec.estimated_cost_impact}
                              </span>
                            )}
                            {rec.payback_timeframe && (
                              <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                ROI in {rec.payback_timeframe}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Total Potential Savings */}
                  {results.ai_insights.total_potential_savings && (
                    <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-green-800">Estimated Total Cost Impact</p>
                          <p className="text-sm text-green-700">{results.ai_insights.total_potential_savings}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <BlurredOverlay>
                  <div className="bg-white/60 rounded-lg p-4 border border-violet-100">
                    <h3 className="text-sm font-semibold text-violet-800 mb-3 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-violet-500 rounded-full"></span>
                      AI-Powered Recommendations with ROI Estimates
                    </h3>
                    <div className="space-y-3">
                      {results.ai_insights.enhanced_recommendations.slice(0, 4).map((rec, idx) => (
                        <div key={idx} className="border-l-2 border-violet-300 pl-3 pb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-violet-600 bg-violet-100 px-2 py-0.5 rounded">
                              #{rec.priority || idx + 1}
                            </span>
                            <span className="text-sm font-medium text-gray-800">{rec.recommendation}</span>
                          </div>
                          {rec.rationale && (
                            <p className="text-xs text-gray-600 mb-1">{rec.rationale}</p>
                          )}
                          {rec.evidence_cited && (
                            <p className="text-xs text-violet-600 italic mb-2">
                              📊 Based on: {rec.evidence_cited}
                            </p>
                          )}
                          {(rec.estimated_cost_impact || rec.payback_timeframe) && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {rec.estimated_cost_impact && (
                                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                  💰 {rec.estimated_cost_impact}
                                </span>
                              )}
                              {rec.payback_timeframe && (
                                <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                  ⏱️ ROI in {rec.payback_timeframe}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {results.ai_insights.total_potential_savings && (
                      <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                        <p className="text-xs font-semibold text-green-800">Estimated Total Cost Impact</p>
                        <p className="text-sm text-green-700">{results.ai_insights.total_potential_savings}</p>
                      </div>
                    )}
                  </div>
                </BlurredOverlay>
              )
            )}
          </div>
        )}

        {/* Lifecycle Wheel Visualization */}
        <LifecycleWheel categoryScores={rollup?.category_scores} />

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Category Scorecard */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="category-scorecard">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-5 h-5 text-primary-500" />
              <h2 className="text-lg font-semibold text-gray-900">Category Scorecard</h2>
            </div>
            <div className="space-y-4">
              {Object.entries(rollup?.category_scores || {}).map(([cat, data]) => (
                <div key={cat} className="flex items-center gap-4" data-testid={`score-${cat}`}>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{CATEGORY_NAMES[cat]}</span>
                      <span className="text-sm text-gray-500">
                        Evidence: {data.evidence_strength}/5
                      </span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${getScoreBgColor(data.final_score)}`}
                        style={{ width: `${(data.final_score / 5) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className={`w-14 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${getScoreColor(data.final_score)}`}>
                    {typeof data.final_score === 'number' ? data.final_score.toFixed(2) : '-'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Gaps - Show 2, blur remaining 3 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="top-gaps">
            <div className="flex items-center gap-3 mb-6">
              <TrendingDown className="w-5 h-5 text-red-500" />
              <h2 className="text-lg font-semibold text-gray-900">Top 5 Gaps</h2>
            </div>
            <div className="space-y-3">
              {/* Show first 2 gaps */}
              {rollup?.top_gap_items?.slice(0, 2).map((gap, idx) => (
                <div 
                  key={`${gap.category}-${gap.question}`} 
                  className="flex items-center gap-3 p-3 bg-red-50 rounded-lg"
                  data-testid={`gap-${idx}`}
                >
                  <span className="w-6 h-6 bg-red-200 text-red-700 rounded-full flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {QUESTION_TITLES[gap.category]?.[gap.question] || `Question ${gap.question.toUpperCase()}`}
                    </div>
                    <div className="text-xs text-gray-500">{CATEGORY_NAMES[gap.category]}</div>
                  </div>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${getScoreColor(gap.score)}`}>
                    {gap.score}/5
                  </span>
                </div>
              ))}
              
              {/* Blur remaining 3 gaps unless unlocked */}
              {rollup?.top_gap_items?.length > 2 && (
                isUnlocked ? (
                  rollup.top_gap_items.slice(2).map((gap, idx) => (
                    <div 
                      key={`${gap.category}-${gap.question}`} 
                      className="flex items-center gap-3 p-3 bg-red-50 rounded-lg"
                      data-testid={`gap-${idx + 2}`}
                    >
                      <span className="w-6 h-6 bg-red-200 text-red-700 rounded-full flex items-center justify-center text-sm font-medium">
                        {idx + 3}
                      </span>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {QUESTION_TITLES[gap.category]?.[gap.question] || `Question ${gap.question.toUpperCase()}`}
                        </div>
                        <div className="text-xs text-gray-500">{CATEGORY_NAMES[gap.category]}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-sm font-medium ${getScoreColor(gap.score)}`}>
                        {gap.score}/5
                      </span>
                    </div>
                  ))
                ) : (
                  <BlurredOverlay>
                    <div className="space-y-3">
                      {rollup.top_gap_items.slice(2).map((gap, idx) => (
                        <div 
                          key={`${gap.category}-${gap.question}`} 
                          className="flex items-center gap-3 p-3 bg-red-50 rounded-lg"
                        >
                          <span className="w-6 h-6 bg-red-200 text-red-700 rounded-full flex items-center justify-center text-sm font-medium">
                            {idx + 3}
                          </span>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {QUESTION_TITLES[gap.category]?.[gap.question] || `Question ${gap.question.toUpperCase()}`}
                            </div>
                            <div className="text-xs text-gray-500">{CATEGORY_NAMES[gap.category]}</div>
                          </div>
                          <span className={`px-2 py-1 rounded text-sm font-medium ${getScoreColor(gap.score)}`}>
                            {gap.score}/5
                          </span>
                        </div>
                      ))}
                    </div>
                  </BlurredOverlay>
                )
              )}
            </div>
          </div>
        </div>

        {/* Recommended Next Steps */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8" data-testid="next-steps">
          <div className="flex items-center gap-3 mb-6">
            <Target className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-semibold text-gray-900">Recommended Next Steps</h2>
          </div>
          <div className="space-y-3">
            {rollup?.top_next_steps?.map((step, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-green-50 rounded-lg" data-testid={`step-${idx}`}>
                <span className="w-6 h-6 bg-green-200 text-green-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                  {idx + 1}
                </span>
                <p className="text-gray-700">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recommended Services Based on Gaps - Show 1, blur rest */}
        {(results?.recommended_services?.primary?.length > 0 || results?.recommended_services?.also_consider?.length > 0) && (
          <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl p-6 mb-8 text-white" data-testid="recommended-services">
            <h2 className="text-xl font-bold mb-4">🎯 Recommended Services Based on Your Results</h2>
            
            {isUnlocked ? (
              <>
                {/* Primary Services - Full view when unlocked */}
                <div className="space-y-4">
                  {results.recommended_services.primary?.slice(0, 3).map((service, idx) => (
                    <div key={idx} className="bg-white/15 rounded-lg p-4 backdrop-blur-sm border-l-4 border-accent-400">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-lg text-accent-300">{service.name}</h3>
                        <span className="text-sm bg-white/20 px-2 py-1 rounded">
                          {service.category_name}: {service.score?.toFixed(2)}/5
                        </span>
                      </div>
                      <p className="text-white/90 text-sm mb-3">{service.description}</p>
                      {service.also_addresses?.length > 0 && (
                        <p className="text-white/70 text-xs mb-3">
                          Also addresses: {service.also_addresses.join(', ')}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {service.benefits?.map((benefit, bidx) => (
                          <span key={bidx} className="text-xs bg-white/20 px-2 py-1 rounded-full">
                            ✓ {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Also Consider Services */}
                {results.recommended_services.also_consider?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/20">
                    <h3 className="text-md font-semibold mb-3 text-white/90">You May Also Consider:</h3>
                    <div className="space-y-3">
                      {results.recommended_services.also_consider.slice(0, 2).map((service, idx) => (
                        <div key={idx} className="bg-white/10 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-accent-300">{service.name}</h4>
                            <span className="text-xs bg-white/15 px-2 py-1 rounded">
                              {service.category_name}: {service.score?.toFixed(2)}/5
                            </span>
                          </div>
                          <p className="text-white/80 text-sm">{service.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Show only 1 primary service when locked */}
                <div className="space-y-4">
                  {results.recommended_services.primary?.slice(0, 1).map((service, idx) => (
                    <div key={idx} className="bg-white/15 rounded-lg p-4 backdrop-blur-sm border-l-4 border-accent-400">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-lg text-accent-300">{service.name}</h3>
                        <span className="text-sm bg-white/20 px-2 py-1 rounded">
                          {service.category_name}: {service.score?.toFixed(2)}/5
                        </span>
                      </div>
                      <p className="text-white/90 text-sm mb-3">{service.description}</p>
                      {service.also_addresses?.length > 0 && (
                        <p className="text-white/70 text-xs mb-3">
                          Also addresses: {service.also_addresses.join(', ')}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {service.benefits?.map((benefit, bidx) => (
                          <span key={bidx} className="text-xs bg-white/20 px-2 py-1 rounded-full">
                            ✓ {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Blur remaining services */}
                {(results.recommended_services.primary?.length > 1 || results.recommended_services.also_consider?.length > 0) && (
                  <div className="mt-4 relative">
                    <div className="blur-sm pointer-events-none select-none">
                      <div className="space-y-4">
                        {results.recommended_services.primary?.slice(1, 3).map((service, idx) => (
                          <div key={idx} className="bg-white/15 rounded-lg p-4 backdrop-blur-sm border-l-4 border-accent-400">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-semibold text-lg text-accent-300">{service.name}</h3>
                              <span className="text-sm bg-white/20 px-2 py-1 rounded">Score</span>
                            </div>
                            <p className="text-white/90 text-sm mb-3">{service.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-transparent via-primary-600/80 to-primary-700/95 rounded-lg">
                      <div className="text-center p-6">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Lock className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">
                          {(results.recommended_services.primary?.length - 1) + (results.recommended_services.also_consider?.length || 0)} More Services Recommended
                        </h3>
                        <p className="text-white/80 text-sm mb-4">
                          Schedule a consultation to see your complete service recommendations
                        </p>
                        <a
                          href={`mailto:sales@fmiqs.com?subject=${encodeURIComponent('Request for Full Service Recommendations')}&body=${encodeURIComponent('Hi FMiQ Team,\n\nI completed the FM Operations Readiness Survey and would like to discuss the full list of recommended services for my organization.\n\nPlease let me know your available times for a 30-minute consultation.\n\nThank you!')}`}
                          className="inline-flex items-center gap-2 px-5 py-2 bg-action-amber hover:bg-amber-500 text-white font-semibold rounded-lg transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          Contact Us for Full Report
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            
            <div className="mt-6 text-center">
              <a 
                href="mailto:jon.eldridge@fmiq.ai?subject=FMiQ%20Services%20Inquiry"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-400 hover:bg-accent-500 text-primary-900 font-semibold rounded-lg transition-all"
              >
                Discuss These Solutions With Us
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}

        {/* Technology Recommendation Section */}
        {results?.technology_recommendation && (
          <div 
            className={`rounded-xl p-6 mb-8 ${
              results.technology_recommendation.type === 'new_platform' 
                ? 'bg-gradient-to-br from-emerald-500 to-emerald-700' 
                : results.technology_recommendation.type === 'system_optimization'
                ? 'bg-gradient-to-br from-blue-500 to-blue-700'
                : 'bg-gradient-to-br from-violet-500 to-violet-700'
            } text-white`}
            data-testid="tech-recommendation"
          >
            <div className="flex items-center gap-3 mb-4">
              {results.technology_recommendation.type === 'new_platform' ? (
                <Cpu className="w-6 h-6" />
              ) : results.technology_recommendation.type === 'system_optimization' ? (
                <Settings className="w-6 h-6" />
              ) : (
                <Search className="w-6 h-6" />
              )}
              <h2 className="text-xl font-bold">{results.technology_recommendation.headline}</h2>
            </div>
            
            <div className="bg-white/15 rounded-lg p-5 backdrop-blur-sm border-l-4 border-white/40">
              <h3 className="font-semibold text-lg text-yellow-300 mb-1">
                {results.technology_recommendation.recommendation?.name}
              </h3>
              <p className="text-white/70 text-sm italic mb-3">
                {results.technology_recommendation.recommendation?.tagline}
              </p>
              <p className="text-white/95 mb-4">
                {results.technology_recommendation.recommendation?.description}
              </p>
              
              {results.technology_recommendation.recommendation?.benefits?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {results.technology_recommendation.recommendation.benefits.map((benefit, idx) => (
                    <span key={idx} className="text-xs bg-white/20 px-3 py-1 rounded-full">
                      ✓ {benefit}
                    </span>
                  ))}
                </div>
              )}
              
              {results.technology_recommendation.bundle_message && (
                <div className="bg-yellow-400/20 rounded-lg p-3 mt-3 border border-yellow-400/30">
                  <p className="text-sm">
                    <span className="font-semibold text-yellow-300">💡 Bundle Opportunity: </span>
                    {results.technology_recommendation.bundle_message}
                  </p>
                </div>
              )}
            </div>
            
            <p className="mt-4 text-white/80 text-sm italic">
              {results.technology_recommendation.reasoning}
            </p>
            
            <div className="mt-5 text-center">
              <a 
                href={`mailto:jon.eldridge@fmiq.ai?subject=${encodeURIComponent(
                  results.technology_recommendation.type === 'new_platform' 
                    ? 'FMiQ One Platform Inquiry' 
                    : 'System Optimization Inquiry'
                )}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg transition-all border border-white/30"
              >
                Learn More About {results.technology_recommendation.recommendation?.name}
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 text-white text-center" data-testid="cta-section">
          <h2 className="text-2xl font-bold mb-3">Ready for a Comprehensive Assessment?</h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            This survey provides a high-level view of your FM readiness. For a detailed Facility Condition Assessment (FCA) 
            with on-site verification, cost modeling, and a multi-year capital plan, contact our team.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="mailto:jon.eldridge@fmiq.ai?subject=FCA%20Consultation%20Request"
              className="px-8 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition-all" 
              data-testid="contact-btn"
            >
              Request FCA Consultation
            </a>
            <button 
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="px-8 py-3 bg-white/20 text-white font-semibold rounded-lg hover:bg-white/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="download-btn-2"
            >
              <Download className="w-5 h-5" />
              Download Report
            </button>
          </div>
        </div>

        {/* Detailed Breakdown (Collapsible) */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Detailed Category Breakdown</h2>
          <div className="space-y-4">
            {Object.entries(results?.rubric_scores || {}).map(([cat, data]) => (
              <details key={cat} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid={`detail-${cat}`}>
                <summary className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${getScoreColor(data.final_score)}`}>
                      {typeof data.final_score === 'number' ? data.final_score.toFixed(2) : '-'}
                    </span>
                    <div>
                      <div className="font-semibold text-gray-900">{CATEGORY_NAMES[cat]}</div>
                      <div className="text-sm text-gray-500">
                        Evidence Strength: {results?.category_evidence_strength?.[cat] || 0}/5
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </summary>
                <div className="border-t border-gray-100 p-4">
                  <div className="grid gap-3">
                    {['a', 'b', 'c', 'd', 'e'].map(q => (
                      <div key={q} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-700 mr-2">{q.toUpperCase()}.</span>
                          <span className="text-gray-600">{QUESTION_TITLES[cat]?.[q] || `Question ${q.toUpperCase()}`}</span>
                        </div>
                        <span className={`px-3 py-1 rounded-lg text-sm font-medium ${getScoreColor(data[q])}`}>
                          {data[q] || '-'}/5
                        </span>
                      </div>
                    ))}
                  </div>
                  {data.notes && Object.values(data.notes).some(n => n) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                      {Object.entries(data.notes).map(([q, note]) => note && (
                        <div key={q} className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">{q.toUpperCase()}:</span> {note}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-primary-900 text-white py-8 mt-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-gray-400">© 2026 FMiQ. Facility Management Intelligence.</p>
          <p className="text-sm text-gray-500 mt-2">
            Session ID: {sessionId}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default ResultsPage;
